// ─── Does this article touch the portfolio? ──────────────────────────────────
//
// "בתיק שלך" on a card is a claim that the article is ABOUT a holding. The
// rule that backed it until 2026-09-24 accepted a company's name anywhere in
// the headline or the summary, and the lead story on the news screen became a
// ResearchAndMarkets press release selling a "Formula 1 Business Outlook
// 2026" report · marked AAPL because "Apple" appeared in its summary. A
// mention is not a subject.
//
// The rule now, one place for every screen (news, home, company page):
//   · a press release never matches. A wire release is an advertisement for
//     whatever the issuer is selling, and naming a big company in it is how
//     it gets found.
//   · the ticker as a word, or the company's own first name, IN THE HEADLINE.
//     A mention in the body only is not enough.
//   · or Finnhub's own `related` tag · but only on the general market feed.
//     On a company feed `related` is the symbol we asked for, echoed back on
//     every article, so it says nothing about the article (DECISIONS.md,
//     2026-09-24, part 0).

// Wire services that carry issuer-paid releases, and the report resellers
// that publish through them. Specific names only: "newswire" on its own would
// also catch Dow Jones Newswires, which is reporting.
const PRESS_RE = /globe\s*newswire|pr\s*newswire|business\s*wire|researchandmarkets|research\s+and\s+markets|accesswire|access\s+newswire|newsfile\s*corp|ein\s*presswire|prweb|newsdirect|pr\.com\b/i;

export function isPressRelease(article) {
  if (!article) return false;
  const hay = [article.source, article.url, article.title, (article.excerpt || "").slice(0, 240)]
    .filter(Boolean).join(" ");
  return PRESS_RE.test(hay);
}

// What to look for in a headline: the company's first real word ("Apple Inc"
// → apple, "NVIDIA Corp" → nvidia) · unless that word is an ordinary word
// headlines are full of. "Bank of America" → "bank" marked every central-bank
// story as BAC, and "General Electric" → "general" matched anything general
// (review, 2026-09-25). For those, the name's first words as a phrase.
const SUFFIX  = /^(inc|corp|corporation|co|company|ltd|plc|the|group|holdings?|sa|nv|ag|se)$/i;
const GENERIC = new Set(["bank", "general", "american", "america", "united", "first", "international",
  "national", "global", "royal", "new", "capital", "home", "public", "applied", "advanced",
  "western", "eastern", "southern", "northern", "pacific", "texas", "energy", "health", "financial"]);

function nameKey(name) {
  const words = String(name || "").split(/[\s,.]+/).filter(w => w && !SUFFIX.test(w));
  const i = words.findIndex(w => w.length >= 4);
  if (i < 0) return "";
  if (!GENERIC.has(words[i].toLowerCase())) return words[i];
  const phrase = words.slice(i, i + 3);
  return phrase.length >= 2 ? phrase.join(" ") : "";
}

// The headline names the company: the ticker as a whole word (3+ letters, so
// "A" and "GE" do not match every sentence), or the first word of its name.
export function headlineNames(article, symbol, name) {
  const text = String(article?.title || "").toLowerCase();
  if (!text) return false;
  const sym = String(symbol || "").toLowerCase();
  if (sym.length >= 3 && new RegExp(`(^|[^a-z])${sym}([^a-z]|$)`).test(text)) return true;
  const key = nameKey(name).toLowerCase();
  if (!key) return false;
  // Whole word at both ends, with an optional possessive: "Meta's" yes,
  // "metals" no; "Visa" yes, "visas" no.
  const esc = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^a-z])${esc}('s|’s)?([^a-z]|$)`).test(text);
}

// Which of `symbols` the article is about. `names` maps symbol → company name.
export function portfolioMatches(article, symbols = [], names = {}) {
  if (!article || isPressRelease(article)) return [];
  const want = new Set(symbols);
  const out = new Set();
  if (article.feed !== "company") {
    for (const s of article.symbols || []) if (want.has(s)) out.add(s);
  }
  for (const s of want) {
    if (!out.has(s) && headlineNames(article, s, names[s])) out.add(s);
  }
  return [...out];
}

// One symbol, one article · the company page and the home screen's list.
export function isAbout(article, symbol, name) {
  return portfolioMatches(article, [symbol], { [symbol]: name }).length > 0;
}

// ─── The same rule for a geopolitical event or an official document ─────────
// A geo_events row carries `tickers` from our own classifier. On 25.09 that
// list put a story about NEAR and Ondo Finance under AAPL · the classifier's
// guess, like Finnhub's `related` on a company feed, is not the article
// naming the company. So an event touches a holding only when its headline,
// in either language, names the ticker or the company. `title` covers the
// calendar, whose rows carry the headline under that name.
export function eventNames(ev, symbol, name) {
  if (!ev) return false;
  const titles = [ev.headline, ev.headline_he, ev.title].filter(t => typeof t === "string" && t.trim());
  return titles.some(t => headlineNames({ title: t }, symbol, name));
}
